import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ref, update } from 'firebase/database';
import { toast } from 'react-toastify';
import {
    FiCamera,
    FiDownload,
    FiEye,
    FiFileText,
    FiLock,
    FiRefreshCw,
    FiRotateCw,
    FiTrash2,
} from 'react-icons/fi';

import { db } from '../../firebase';
import { deleteFileFromWasabi } from '../../utils/wasabiUploader';

// Controls tab — primary device actions (remote view, reboot, OTA toggle) +
// Diagnostics (screenshot / logs + reported state). Moved out of the Overview
// tab so Overview stays purely "what is this device?".

export default function ControlsTab({ mac, device }) {
    return (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">
            <ControlsCard mac={mac} device={device} />
            <DiagnosticsCard mac={mac} device={device} />
        </div>
    );
}

// ---------------------------------------------------------------------
// Shared primitives (kept local to this tab)
// ---------------------------------------------------------------------

function Divider({ className = 'my-1' }) {
    return <div className={`${className} border-t border-white/[0.04]`} aria-hidden="true" />;
}

function StatePill({ label, value, tone = 'neutral' }) {
    const tones = {
        neutral: 'bg-white/[0.05] border-white/[0.08] text-gray-200',
        good:    'bg-green-500/10 border-green-500/30 text-green-300',
        warning: 'bg-yellow-500/10 border-yellow-500/30 text-yellow-300',
        danger:  'bg-red-500/10 border-red-500/30 text-red-300',
    };
    return (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${tones[tone]}`}>
            <span className="text-gray-400">{label}</span>
            <span>{value}</span>
        </span>
    );
}

// ---------------------------------------------------------------------
// Controls — remote view, reboot, OTA toggle.
// ---------------------------------------------------------------------

function ControlsCard({ mac, device }) {
    const navigate = useNavigate();
    const otaEnabled = device?.ota_enabled !== false;

    const reboot = async () => {
        if (!window.confirm('Reboot this device now?')) return;
        try {
            await update(ref(db, `mdm/devices/${mac}`), {
                command: 'reboot',
                feedback: 'pending',
            });
            toast.success('Reboot requested');
        } catch (err) {
            toast.error(`Reboot failed: ${err.message}`);
        }
    };

    // Screen rotation policy. Written to mdm/devices/<MAC>/rotation_lock and applied by Monitoring 6.0.5+
    // as persist.sys.app.rotation: absent/anything -> "original" (the Player rotates/flips signage per
    // playlist), "landscape" -> "force_land" (OEM lock, every app request ignored). The field is REMOVED
    // rather than written for the default, so unmanaged boxes and managed-default boxes look the same.
    const rotationLocked = device?.rotation_lock === 'landscape';

    const toggleRotationLock = async () => {
        const next = !rotationLocked;
        try {
            await update(ref(db, `mdm/devices/${mac}`), { rotation_lock: next ? 'landscape' : null });
            toast.success(next
                ? 'Screen locked to landscape (Monitoring applies it and restarts the player)'
                : 'Player controls screen rotation (portrait / flip per playlist)');
        } catch (err) {
            toast.error(`Failed to change rotation policy: ${err.message}`);
        }
    };

    // Kiosk mode (Monitoring 6.0.6+): kiosk_mode = what we ask for, kiosk_state = what the box applied
    // ("on" / "off" / "failed"). Always written as an explicit true/false, never removed: the box only
    // reacts to an existing field, so "off" has to be written.
    const kioskOn = device?.kiosk_mode === true;

    const toggleKiosk = async () => {
        const next = !kioskOn;
        try {
            await update(ref(db, `mdm/devices/${mac}`), { kiosk_mode: next });
            toast.success(next
                ? 'Kiosk mode on - HOME and BACK return to the player'
                : 'Kiosk mode off - normal launcher restored');
        } catch (err) {
            toast.error(`Failed to change kiosk mode: ${err.message}`);
        }
    };

    const toggleOta = async () => {
        const next = device?.ota_enabled === false;
        try {
            await update(ref(db, `mdm/devices/${mac}`), { ota_enabled: next });
            toast.success(`OTA updates ${next ? 'enabled' : 'disabled'}`);
        } catch (err) {
            toast.error(`Failed to toggle OTA: ${err.message}`);
        }
    };

    return (
        <section className="card-chrome p-5 shadow-sm h-full">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400">Controls</h2>
            </header>

            <div className="grid grid-cols-2 gap-2">
                <ActionTile
                    onClick={() => navigate(`/remote?device=${encodeURIComponent(mac)}`)}
                    icon={<FiEye />}
                    label="Remote Control"
                    hint="Open screen viewer"
                />
                <ActionTile
                    onClick={reboot}
                    icon={<FiRefreshCw />}
                    label="Reboot"
                    hint="Restart the device"
                    tone="danger"
                />
            </div>

            <Divider className="my-4" />

            <div className="card-inset p-4 flex items-center justify-between gap-3">
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium text-white">
                        <FiDownload className="text-brand-400" /> OTA Updates
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        Auto-pull firmware as it ships
                    </div>
                </div>
                <button
                    onClick={toggleOta}
                    aria-pressed={otaEnabled}
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition shrink-0 ${
                        otaEnabled
                            ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                            : 'bg-red-500/15 text-red-400 hover:bg-red-500/25'
                    }`}
                >
                    {otaEnabled ? 'Enabled' : 'Disabled'}
                </button>
            </div>

            <div className="card-inset p-4 flex items-center justify-between gap-3 mt-2">
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium text-white">
                        <FiRotateCw className="text-brand-400" /> Screen Rotation
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {rotationLocked
                            ? 'Locked to landscape - playlist orientation and flip are ignored'
                            : 'Player controls - portrait and 180 flip follow the signage playlist'}
                        {device?.hw_fingerprint?.app_rotation && (
                            <span className="font-mono"> · box: {device.hw_fingerprint.app_rotation}</span>
                        )}
                    </div>
                </div>
                <button
                    onClick={toggleRotationLock}
                    aria-pressed={rotationLocked}
                    title="Needs Monitoring 6.0.5 or newer on the box"
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition shrink-0 ${
                        rotationLocked
                            ? 'bg-yellow-500/15 text-yellow-400 hover:bg-yellow-500/25'
                            : 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                    }`}
                >
                    {rotationLocked ? 'Locked landscape' : 'Player controls'}
                </button>
            </div>

            <div className="card-inset p-4 flex items-center justify-between gap-3 mt-2">
                <div className="min-w-0">
                    <div className="inline-flex items-center gap-1.5 text-sm font-medium text-white">
                        <FiLock className="text-brand-400" /> Kiosk Mode
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">
                        {kioskOn
                            ? 'HOME and BACK always return to the player'
                            : 'Normal launcher - HOME leaves the player'}
                        {device?.kiosk_state && (
                            <span className={`font-mono ${device.kiosk_state === 'failed' ? 'text-red-400' : ''}`}>
                                {' '}· box: {device.kiosk_state}
                            </span>
                        )}
                    </div>
                </div>
                <button
                    onClick={toggleKiosk}
                    aria-pressed={kioskOn}
                    title="Needs Monitoring 6.0.6 or newer on the box"
                    className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium transition shrink-0 ${
                        kioskOn
                            ? 'bg-green-500/15 text-green-400 hover:bg-green-500/25'
                            : 'bg-white/[0.05] text-gray-300 hover:bg-white/[0.10]'
                    }`}
                >
                    {kioskOn ? 'On' : 'Off'}
                </button>
            </div>
        </section>
    );
}

function ActionTile({ onClick, icon, label, hint, tone = 'neutral' }) {
    const tones = {
        neutral: { text: 'text-gray-400',  hover: 'hover:bg-white/[0.06]' },
        danger:  { text: 'text-red-400',   hover: 'hover:bg-red-500/10 hover:border-red-500/40' },
    };
    const c = tones[tone] || tones.neutral;
    return (
        <button
            onClick={onClick}
            className={`bg-surface-inset border border-white/[0.08] rounded-lg p-4 text-left transition-colors ${c.hover}`}
        >
            <div className={`inline-flex items-center gap-1.5 ${c.text} text-xs uppercase tracking-wider`}>
                {icon} {label}
            </div>
            <div className="mt-1 text-sm font-medium text-white">{hint}</div>
        </button>
    );
}

// ---------------------------------------------------------------------
// Diagnostics — screenshot / logs + reported boolean state flags.
// ---------------------------------------------------------------------

function DiagnosticsCard({ mac, device }) {
    if (!device) return null;

    const flags = [];
    const has = (key) => device[key] !== undefined && device[key] !== null;

    if (has('isLocked'))               flags.push({ label: 'Lock screen', value: device.isLocked ? 'Locked' : 'Unlocked' });
    if (has('isSleeping'))             flags.push({ label: 'Sleep',       value: device.isSleeping ? 'Sleeping' : 'Awake' });
    if (has('wifiEnabled'))            flags.push({ label: 'Wi-Fi',       value: device.wifiEnabled ? 'On' : 'Off',          tone: device.wifiEnabled ? 'good' : 'neutral' });
    if (has('mobileDataEnabled'))      flags.push({ label: 'Mobile Data', value: device.mobileDataEnabled ? 'On' : 'Off' });
    if (has('hotspotEnabled'))         flags.push({ label: 'Hotspot',     value: device.hotspotEnabled ? 'On' : 'Off' });
    if (has('kiosk_state'))            flags.push({ label: 'Kiosk',       value: device.kiosk_state === 'on' ? 'On' : device.kiosk_state === 'failed' ? 'Failed' : 'Off', tone: device.kiosk_state === 'on' ? 'good' : device.kiosk_state === 'failed' ? 'danger' : 'neutral' });
    if (has('navigationButtonsBlocked')) flags.push({ label: 'Nav buttons', value: device.navigationButtonsBlocked ? 'Blocked' : 'Allowed' });
    if (has('isRooted'))               flags.push({ label: 'Rooted',      value: device.isRooted ? 'Yes' : 'No',             tone: device.isRooted ? 'danger' : 'good' });
    if (has('servicePermissions')) {
        const allGranted = device.servicePermissions.allGranted;
        flags.push({
            label: 'Permissions',
            value: allGranted ? 'All granted' : 'Missing',
            tone: allGranted ? 'good' : 'danger',
        });
    }

    const trigger = (cmd, name) => async () => {
        try {
            await update(ref(db, `mdm/devices/${mac}`), {
                command: cmd,
                feedback: 'pending',
            });
            toast.success(`${name} requested`);
        } catch (err) {
            toast.error(`${name} failed: ${err.message}`);
        }
    };

    const deleteArtifact = (kind) => async () => {
        const fileUrl = kind === 'screenshot' ? device.last_screenshot : device.last_log;
        if (!fileUrl) {
            toast.error(`No ${kind} to delete`);
            return;
        }
        if (!window.confirm(`Delete the last ${kind}? This removes the file from Wasabi and clears the dashboard reference.`)) return;

        try {
            await deleteFileFromWasabi(fileUrl);
            const updates = kind === 'screenshot'
                ? { last_screenshot: null, last_screenshot_time: null }
                : { last_log: null, last_log_time: null };
            await update(ref(db, `mdm/devices/${mac}`), updates);
            toast.success(`${kind === 'screenshot' ? 'Screenshot' : 'Log'} deleted`);
        } catch (err) {
            toast.error(`Failed to delete ${kind}: ${err.message}`);
        }
    };

    const screenshotDate = device.last_screenshot_time
        ? new Date(device.last_screenshot_time).toLocaleString()
        : null;
    const logDate = device.last_log_time
        ? new Date(device.last_log_time).toLocaleString()
        : null;

    return (
        <section className="card-chrome p-5 shadow-sm h-full">
            <header className="mb-4">
                <h2 className="text-base font-semibold text-brand-400">Diagnostics</h2>
            </header>

            <div className="grid grid-cols-2 gap-2">
                <DiagnosticTile
                    icon={<FiCamera />}
                    label="Last Screenshot"
                    href={device.last_screenshot}
                    timestamp={screenshotDate}
                    actions={[
                        { onClick: trigger('screenshot', 'Screenshot'), icon: <FiCamera />, text: 'Take new' },
                        ...(device.last_screenshot ? [{
                            onClick: deleteArtifact('screenshot'),
                            icon: <FiTrash2 />,
                            text: 'Delete',
                            tone: 'danger',
                        }] : []),
                    ]}
                />
                <DiagnosticTile
                    icon={<FiFileText />}
                    label="Last Logs"
                    href={device.last_log}
                    timestamp={logDate}
                    actions={[
                        { onClick: trigger('logs:500', 'Logs (500)'), icon: <FiFileText />, text: 'Pull 500' },
                        { onClick: trigger('logs:2000', 'Logs (2000)'), icon: <FiFileText />, text: 'Pull 2000' },
                        ...(device.last_log ? [{
                            onClick: deleteArtifact('log'),
                            icon: <FiTrash2 />,
                            text: 'Delete',
                            tone: 'danger',
                        }] : []),
                    ]}
                />
            </div>

            <Divider className="my-4" />

            <div>
                <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-2">Reported state</div>
                {flags.length === 0 ? (
                    <p className="text-sm text-gray-500">
                        This device hasn't reported any state flags yet.
                    </p>
                ) : (
                    <div className="flex flex-wrap gap-2">
                        {flags.map((f) => (
                            <StatePill key={f.label} {...f} />
                        ))}
                    </div>
                )}
            </div>
        </section>
    );
}

function DiagnosticTile({ icon, label, href, timestamp, actions, tone = 'neutral' }) {
    const tones = {
        neutral: {
            text: 'text-gray-400',
            hover: 'hover:bg-white/[0.06]',
            btn: 'bg-white/[0.05] hover:bg-white/[0.08] text-gray-200',
        },
    };
    const c = tones[tone] || tones.neutral;
    return (
        <div className={`bg-surface-inset border border-white/[0.08] rounded-lg p-4 transition-colors ${c.hover}`}>
            <div className={`inline-flex items-center gap-1.5 ${c.text} text-xs uppercase tracking-wider`}>
                {icon} {label}
            </div>

            <div className="mt-1 text-sm">
                {href ? (
                    <a
                        href={href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-brand-400 hover:text-brand inline-flex items-baseline gap-1.5 max-w-full"
                    >
                        <span className="font-medium">View</span>
                        {timestamp && (
                            <span className="text-xs text-gray-500 truncate">({timestamp})</span>
                        )}
                    </a>
                ) : (
                    <span className="text-gray-500">none yet</span>
                )}
            </div>

            <div className="mt-3 flex flex-wrap gap-1.5">
                {actions.map(({ onClick, icon: btnIcon, text, tone: btnTone }) => {
                    const cls = btnTone === 'danger'
                        ? 'bg-red-500/10 hover:bg-red-500/20 text-red-300 border border-red-500/30'
                        : c.btn;
                    return (
                        <button
                            key={text}
                            onClick={onClick}
                            className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${cls}`}
                        >
                            {btnIcon} {text}
                        </button>
                    );
                })}
            </div>
        </div>
    );
}
